import { useState, useEffect, useCallback, useRef } from 'react';
import { WebSocketCallService } from '../services/websocketCallService/WebSocketCallService';
import { WebSocketCallServiceConfig } from '@shared/services/websocketCallService';
import { CallTranscriptData, TranscriptActor } from '@shared/services/websocketCallService/types';
import { currentTimestamp } from '@shared/utils/DateUtil';
import { ConversationLogDto } from '@shared/services/conversationApiService/apiService';

export interface WebSocketTranscript extends ConversationLogDto {
  firstReceivedTime?: number;
  lastReceivedTime?: number;
}

export interface UseWebSocketCallServiceReturn {
  isConnected: boolean;
  isSessionReady: boolean;
  isRecording: boolean;
  callId: string | null;
  error: string | null;
  transcripts: WebSocketTranscript[] | null;

  startCall: () => Promise<void>;
  endCall: () => Promise<void>;
  disconnect: () => void;

  onTranscriptReceived: (callback: (text: string, isFinal: boolean) => void) => void;
}

export const useWebSocketCallService = (
  config: WebSocketCallServiceConfig,
): UseWebSocketCallServiceReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<WebSocketTranscript[] | null>([]);

  const serviceRef = useRef<WebSocketCallService | null>(null);
  const transcriptCallbackRef = useRef<((text: string, final: boolean) => void) | null>(null);
  const callIdRef = useRef<string | null>(null);

  // Id of the not-yet-finalized transcript entry per side of the conversation.
  // Agent and customer speak in parallel, so each side needs its own open entry —
  // otherwise their chunks get merged into a single message.
  const openEntryIdRef = useRef<Partial<Record<TranscriptActor, string>>>({});
  const entryIdBySegmentRef = useRef<Record<string, string>>({});
  const nextEntryIdRef = useRef(0);

  const resetTranscriptState = useCallback(() => {
    openEntryIdRef.current = {};
    entryIdBySegmentRef.current = {};
    nextEntryIdRef.current = 0;
    setTranscripts(null);
  }, []);

  useEffect(() => {
    const svc = new WebSocketCallService({
      ...config,

      onConnectedToServer: () => {
        setIsConnected(true);
      },

      onSessionReady: (data) => {
        setIsSessionReady(true);
        callIdRef.current = data.callId;
        setCallId(data.callId);
      },

      onTranscriptReceived: (data: CallTranscriptData) => {
        // Fire external callback if provided
        transcriptCallbackRef.current?.(data.text, !!data.final);

        const receivedAt = currentTimestamp().valueOf();
        const now = new Date(receivedAt).toISOString();
        const { actor, text, final, segmentId } = data;
        const openEntryId = openEntryIdRef.current[actor];
        const revisedEntryId = segmentId ? entryIdBySegmentRef.current[segmentId] : undefined;
        const startsRevision = !openEntryId && revisedEntryId !== undefined;

        // A final chunk closes this actor's utterance; the next one starts a new bubble
        if (final) {
          delete openEntryIdRef.current[actor];
        }

        // End-of-utterance markers carry no text and must not create an empty bubble
        if (!text) return;

        // Reuse the open entry of this actor, or start a new one at the end of the list
        const entryId = revisedEntryId ?? openEntryId ?? String(nextEntryIdRef.current++);
        if (!final) {
          openEntryIdRef.current[actor] = entryId;
        }
        if (segmentId) {
          entryIdBySegmentRef.current[segmentId] = entryId;
        }

        setTranscripts((prev) => {
          const list = prev ?? [];
          const idx = list.findIndex((l) => l.id === entryId);

          if (idx < 0) {
            const log: WebSocketTranscript = {
              id: entryId,
              conversationId: callIdRef.current ?? '',
              actor,
              message: text,
              createdAt: now,
              updatedAt: now,
              firstReceivedTime: receivedAt,
              lastReceivedTime: receivedAt,
            };
            return [...list, log];
          }

          const current = list[idx];
          const next = [...list];
          next[idx] = {
            ...current,
            message: startsRevision || !current.message ? text : `${current.message} ${text}`,
            updatedAt: now,
            lastReceivedTime: receivedAt,
          };
          return next;
        });
      },

      onError: (err) => {
        console.error('WebSocket Call Service error:', err);
        setError(err?.message ?? 'Unknown error');
      },

      onDisconnect: () => {
        setIsConnected(false);
        setIsSessionReady(false);
        setIsRecording(false);
        callIdRef.current = null;
        setCallId(null);
        resetTranscriptState();
      },
    });

    serviceRef.current = svc;

    return () => {
      svc.destroy();
      serviceRef.current = null;
    };
  }, []);

  // Start: connect WS + init audio + start capture/stream
  const startCall = useCallback(async () => {
    if (!serviceRef.current) throw new Error('Service not initialized');
    setError(null);
    await serviceRef.current.startCall();
    setIsRecording(true);
  }, []);

  const endCall = useCallback(async () => {
    if (!serviceRef.current) return;
    await serviceRef.current.endCall();
    setIsSessionReady(false);
    setIsRecording(false);
    callIdRef.current = null;
    setCallId(null);
    resetTranscriptState();
    setIsConnected(false);
  }, [resetTranscriptState]);

  const disconnect = useCallback(() => {
    serviceRef.current?.destroy();
    serviceRef.current = null;
    setIsConnected(false);
    setIsSessionReady(false);
    setIsRecording(false);
    callIdRef.current = null;
    setCallId(null);
    resetTranscriptState();
  }, [resetTranscriptState]);

  const onTranscriptReceived = useCallback((cb: (text: string, final: boolean) => void) => {
    transcriptCallbackRef.current = cb;
  }, []);

  return {
    isConnected,
    isSessionReady,
    isRecording,
    callId,
    error,
    transcripts,
    startCall,
    endCall,
    disconnect,
    onTranscriptReceived,
  };
};
